import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, inject, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  FormLayoutComponent,
  IFormLayout,
  IFormLayoutContent,
  IFormLayoutFooter,
  IFormLayoutHeader,
  IPolymorphicControlMeta,
  // PolymorphicControlService,
  PolyMorphicControlTypes,
  PolymorphicControlComponent,
  ToastService,
  OffCanvasPlacement,
  closeOffCanvas,
  OptimaOffCanvasService,
  PolymorphicControlService
} from '@optima-stride-ai/optima-components';
import { IBizEntity, IAdditionalInfo, BizEntityTypeCategory, ItxnRelationshipLog, BizEntityFunctionTypes, FieldTypes } from '@optima-stride-ai/core';
import { OffcanvasService, CardComponent, CardBodyComponent } from '@coreui/angular';
import { AppStateService } from '../../state/app-state';
import { TxnLogStateStoreService } from '../../transaction-log/txn-log-state-store/txn-log-state-store.service';
import { MeasureLogStateStoreService } from '../measure-log-state-store/measure-log-state-store.service';
import { FormStates } from '../../off-canvas/off-canvas-service/off-canvas-form.service';
import { UtilService } from '../../utils/util.service';
import { createEmptyMeasureLogJSON, createEmptyTxnLogJSON } from '../txn-log';
import { IMeasure, IMeasureLog } from '../../transaction(Measure)/measure.model';
import { ModalButtonIDs } from '../../biz-entity/form-base-entity/form-base-entity.component';
import { Explorers, GetDefaultConfig, launchExplorerDialog, ModalDialogs, ModalDialogSizes } from '../../modal/modal';
import { OptimaDefaultModalService } from '../../modal/modal.service';

@Component({
  selector: 'app-form-transaction-log',
  imports: [FormLayoutComponent, PolymorphicControlComponent, CardComponent, CardBodyComponent],
  templateUrl: './form-transaction-log.component.html',
  styleUrl: './form-transaction-log.component.sass'
})
export class FormTransactionLogComponent implements OnInit, OnDestroy {
  // Public properties
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() isInModal: boolean = false;
  // Header properties
  headerTitle: string = '';
  headerSubtitle: string = '';
  
  // Form layouts
  formHeaderLayout: IFormLayout = { 
    header: { title: '' }, 
    content: { fieldItems: [] }, 
    footer: { actions: [] } 
  };
  private _formFooterLayout: IFormLayout = { 
    header: { title: '' }, 
    content: { fieldItems: [] }, 
    footer: { actions: [] } 
  };

  get formFooterLayout(): IFormLayout {
    if (this.isEditing) {
      return { ...this._formFooterLayout, footer: { ...this._formFooterLayout.footer, actions: [] } };
    }
    const actions = (this._formFooterLayout.footer?.actions ?? []).map((action, i) =>
      i === 0 ? { ...action, isDisabled: !this.isFormValid || this.isAPICallInProgress } : action
    );
    return { ...this._formFooterLayout, footer: { ...this._formFooterLayout.footer, actions } };
  }
  
  // Polymorphic controls
  polyDateControl!: IPolymorphicControlMeta;
  additionalFieldPolyControlMeta: IPolymorphicControlMeta[] = [];

  // Private properties
  private transactionLog!: ItxnRelationshipLog;
  private measureLog!: IMeasureLog;
  private measure!: IMeasure;
  private bizEntity!: IBizEntity;
  private isEditing: boolean = false;
  private isAPICallInProgress: boolean = false;
  private subscriptions = new Subscription();

  // Services
  private readonly polymorphicControlService = inject(PolymorphicControlService);
  private readonly appStateService = inject(AppStateService);
  private readonly utilService = inject(UtilService);
  private readonly measureLogStateStoreService = inject(MeasureLogStateStoreService);
  private readonly txnLogStateStoreService = inject(TxnLogStateStoreService);
  private readonly toastService = inject(ToastService);
  private readonly offCanvasFormService = inject(OptimaOffCanvasService);
  private readonly offcanvasService = inject(OffcanvasService);
  private readonly modalService = inject(OptimaDefaultModalService);
  
  constructor() {
    this.isEditing = this.appStateService.entityFormState === FormStates.Edit;
  }

  ngOnInit(): void {
    if (this.isEditing) {
      this.initializeEditMode();
    } else {
      this.initializeCreateMode();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get isFormValid(): boolean {
    if (this.polyDateControl?.fieldMeta?.isRequired) {
      const dateValue = this.polyDateControl.value;
      if (!dateValue || dateValue.trim() === '') return false;
      if (this.polyDateControl.fieldMeta.inputPattern) {
        if (!new RegExp(this.polyDateControl.fieldMeta.inputPattern).test(dateValue)) return false;
      }
    }

    for (const field of this.additionalFieldPolyControlMeta ?? []) {
      const fieldValue = field.type === PolyMorphicControlTypes.explorer
        ? field.selectedBizEntity
        : field.type === PolyMorphicControlTypes.options
          ? field.optionSelected
          : field.value;

      if (field.fieldMeta?.isRequired && (fieldValue === null || fieldValue === undefined || fieldValue === '')) {
        return false;
      }

      if (field.fieldMeta?.inputPattern && fieldValue) {
        if (!new RegExp(field.fieldMeta.inputPattern).test(fieldValue.toString())) return false;
      }

      if (field.type === PolyMorphicControlTypes.number && fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
        const numValue = Number(fieldValue);
        if (isNaN(numValue)) return false;
        if (field.fieldMeta?.min !== undefined && field.fieldMeta.min !== null && numValue < field.fieldMeta.min) return false;
        if (field.fieldMeta?.max !== undefined && field.fieldMeta.max !== null && numValue > field.fieldMeta.max) return false;
      }
    }

    return true;
  }
  get validate(): boolean {
    return this.isFormValid;
  }

  get data(): {log: ItxnRelationshipLog, isValid: boolean} {
    // Sync current form values into the model, then return the exact payload
    // that onLogCreate() submits to the state store for the API call.
    this.populateLogFormValues();
    let logToSubmit = {log: this.prepareLogForSubmission(this.isMeasureLog(this.bizEntity)), isValid: this.isFormValid};
    return logToSubmit;
  }

  private touchAllFields(): void {
    if (this.polyDateControl?.fieldMeta?.isRequired) {
      this.polyDateControl.touched = true;
    }
    for (const field of this.additionalFieldPolyControlMeta) {
      if (field.fieldMeta?.isRequired || field.fieldMeta?.inputPattern) {
        field.touched = true;
      }
    }
  }

  private initializeCreateMode(): void {
    const measureSub = this.appStateService.selectedMeasure$.subscribe(measure => {
      if (measure) {
        this.handleMeasureSelection(measure);
      }
    });

    const txnSub = this.appStateService.selectedTransaction$.subscribe(transaction => {
      if (transaction) {
        this.handleTransactionSelection(transaction);
      }
    });

    this.subscriptions.add(measureSub);
    this.subscriptions.add(txnSub);
  }

  private initializeEditMode(): void {
    this.initializeForm();
    this.initializeDateControl();
    this.updateHeaderTitle();
    this._formFooterLayout = this.buildFooterLayout();
  }

  private handleMeasureSelection(measure: IMeasure): void {
    this.bizEntity = measure;
    this.measure = measure;
    this.initializeAndBuildForms();
  }

  private handleTransactionSelection(transaction: IBizEntity): void {
    this.bizEntity = transaction;
    this.initializeAndBuildForms();
  }

  private initializeAndBuildForms(): void {
    this.initializeForm();
    this.initializeDateControl();
    this.updateHeaderTitle();
    this._formFooterLayout = this.buildFooterLayout();
  }

  private initializeForm(): void {
    if (this.isEditing) {
      this.initializeEditingLog();
    } else {
      this.initializeNewLog();
    }
    this.prepareCustomFieldMeta();
  }

  private initializeEditingLog(): void {
    this.transactionLog = this.appStateService.currentSelectedTxnLogItem!;
    this.title = this.transactionLog.bizEntity.name;
  }

  private initializeNewLog(): void {
    if (this.isMeasureLog(this.bizEntity)) {
      this.measureLog = createEmptyMeasureLogJSON(this.measure);
      this.title = this.measureLog?.measure?.name || '';
    } else {
      this.transactionLog = createEmptyTxnLogJSON(this.bizEntity);
      this.title = this.transactionLog.bizEntity.name;
    }
  }

  private initializeDateControl(): void {
    const logDate = this.isMeasureLog(this.bizEntity) ? this.measureLog?.date : this.transactionLog?.date;
    const todayDate = new Date();
    this.polyDateControl = this.polymorphicControlService.getC({
      id: 'date',
      type: PolyMorphicControlTypes.calendar,
      label: 'Date',
      value: this.utilService.convertDateToString(logDate) || this.utilService.convertDateToString(new Date()),
      icon: 'cilCalendar',
      placeholder: 'Select date',
      maxDate: this.utilService.convertDateToString(todayDate),
      fieldMeta: {
        id: 'date',
        name: 'Date',
        type: FieldTypes.rangeDate,
        label: ' Date Field',
        isRequired: true,
      }
    });
  }

  private updateHeaderTitle(): void {
    const action = this.isEditing ? 'Update' : 'Create';
    this.headerTitle = `${action} ${this.title} Log`;
    this.buildHeaderLayout();
  }

  private buildHeaderLayout(): void {
    this.formHeaderLayout = {
      header: {
        title: this.headerTitle,
        subtitle: this.headerSubtitle
      },
      content: { fieldItems: [] },
      footer: { actions: [] }
    };
  }

  private buildFooterLayout(): IFormLayout {
    const header: IFormLayoutHeader = { title: '', subtitle: '' };
    const content: IFormLayoutContent = { fieldItems: [] };
    const footer: IFormLayoutFooter = {
      actions: [
        {
          label: this.isEditing ? `Update ${this.title} Log` : `Create ${this.title} Log`,
          icon: this.isEditing ? 'cilCheckAlt' : 'cilPlus'
        }
      ]
    };
    const cssClass = 'ben-form-transaction-log-footer';
    return { header, content, footer, cssClass };
  }

  private isMeasureLog(bizEntity: IBizEntity): boolean {
    return bizEntity?.type?.category === BizEntityTypeCategory.BenzaitenMeasure;
  }

  private populateLogFormValues(): void {
    const dateValue = this.getDateFromForm();
    
    if (this.isMeasureLog(this.bizEntity)) {
      this.populateMeasureLogValues(dateValue);
    } else {
      this.populateTransactionLogValues(dateValue);
    }
  }

  /** Picked day combined with the real time of day, so the stored value is an exact instant (serialised as UTC). */
  private getDateFromForm(): Date {
    const dateFieldValue = this.polyDateControl?.value;
    if (!dateFieldValue) return new Date();

    const existing = this.isMeasureLog(this.bizEntity) ? this.measureLog?.date : this.transactionLog?.date;
    const existingDate = existing ? new Date(existing) : undefined;
    if (existingDate && this.utilService.getValiDateString(existingDate) === dateFieldValue) {
      return existingDate;
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateFieldValue));
    if (!match) return new Date(dateFieldValue);
    const [year, month, day] = match.slice(1).map(Number);
    const now = new Date();
    return new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
  }

  private populateMeasureLogValues(date: Date): void {
    this.measureLog.date = date;
    this.measureLog.logFieldValues = this.mapToAdditionalInfo(
      this.additionalFieldPolyControlMeta,
      this.measureLog?.logFieldValues
    );
  }

  private populateTransactionLogValues(date: Date): void {
    this.transactionLog.date = date;
    this.transactionLog.logFieldValues = this.mapToAdditionalInfo(
      this.additionalFieldPolyControlMeta,
      this.transactionLog?.logFieldValues
    );
  }

  private mapToAdditionalInfo(
    data: IPolymorphicControlMeta[],
    logFieldValues: IAdditionalInfo[]
  ): IAdditionalInfo[] {
    return data.map((controlMeta, index) => ({
      key: controlMeta.id,
      value: controlMeta.value,
      fieldMeta: logFieldValues[index].fieldMeta
    }));
  }

  private prepareCustomFieldMeta(): void {
    this.additionalFieldPolyControlMeta = [];
    const logFieldValues = this.getLogFieldValues();
    
    if (!logFieldValues) {
      return;
    }

    logFieldValues.forEach((addInfo: IAdditionalInfo) => {
      if (!addInfo.fieldMeta.hiddenInForm) {
        const controlMeta = this.createControlMeta(addInfo);
        this.additionalFieldPolyControlMeta.push(controlMeta);
      }
    });
  }

  private getLogFieldValues(): IAdditionalInfo[] | undefined {
    return this.isMeasureLog(this.bizEntity) 
      ? this.measureLog?.logFieldValues 
      : this.transactionLog?.logFieldValues;
  }

  private createControlMeta(addInfo: IAdditionalInfo): IPolymorphicControlMeta {
    const controlMeta = this.polymorphicControlService.getC();
    controlMeta.type = this.polymorphicControlService.getPolyControlFromFieldType(addInfo.fieldMeta.type);
    
    if(controlMeta.type === PolyMorphicControlTypes.explorer){
      // controlMeta.executeClickCallback = () => { this.openRespectiveExplorer(explorerID); }
      let explorerId = addInfo.fieldMeta.bizEntityExplorerId;
      if(explorerId)
      {
       controlMeta.explorerSelected = explorerId.toString(); 
      }
      controlMeta.executeClickCallback = () => {
        launchExplorerDialog(controlMeta, this.modalService);
      };
    }

    controlMeta.icon = addInfo.fieldMeta.icon || 'cilText';
    controlMeta.value = this.getFieldValue(addInfo, controlMeta.type) as string;
    controlMeta.id = addInfo.fieldMeta.name;
    controlMeta.fieldMeta = addInfo.fieldMeta;
    controlMeta.label = addInfo.fieldMeta.label;
    return controlMeta;
  }

  private getFieldValue(addInfo: IAdditionalInfo, controlType: PolyMorphicControlTypes): unknown {
    if (this.isEditing && controlType === PolyMorphicControlTypes.calendar) {
      return this.utilService.getValiDateString(new Date(addInfo.value));
    }
    return addInfo.value;
  }


  onLogCreate(): void {
    this.touchAllFields();

    if (!this.isFormValid) {
      this.toastService.toast('Please fill all required fields correctly');
      return;
    }

    if (this.isAPICallInProgress) {
      this.toastService.toast('Please wait, operation in progress.');
      return;
    }

    this.setAPICallInProgress(true);
    this.populateLogFormValues();
    
    const isMeasure = this.isMeasureLog(this.bizEntity);
    const logToSubmit = this.prepareLogForSubmission(isMeasure);
    const serviceMethod = this.isEditing ? 'update' : 'create';
    const stateStoreService = isMeasure 
      ? this.measureLogStateStoreService 
      : this.txnLogStateStoreService;

    stateStoreService[serviceMethod](logToSubmit)
      ?.then(() => {
        this.afterSubmissionSucceeded();
        this.setAPICallInProgress(false);
      })
      .catch((error) => {
        this.handleSubmissionError(error, isMeasure);
        this.setAPICallInProgress(false);
      });
  }

  private setAPICallInProgress(inProgress: boolean): void {
    this.isAPICallInProgress = inProgress;
  }

  private prepareLogForSubmission(isMeasure: boolean): ItxnRelationshipLog {
    return isMeasure 
      ? this.populateTxnLogFromMeasureLog(this.measureLog)
      : this.transactionLog;
  }

  private handleSubmissionError(error: Error, isMeasure: boolean): void {
    const logType = isMeasure ? 'measure log' : 'transaction log';
    const operation = this.isEditing ? 'updating' : 'creating';
    // console.error(`Error ${operation} ${logType}:`, error);
    this.toastService.toast(`Error ${operation} ${logType}`);
  }

  private populateTxnLogFromMeasureLog(measureLog: IMeasureLog): ItxnRelationshipLog {
    return {
      id: measureLog.id,
      tenantId: measureLog.tenantId,
      appId: measureLog.appId,
      date: measureLog.date,
      bizEntity: measureLog.measure, // IMeasure extends IBizEntity
      logFieldValues: measureLog.logFieldValues,
      author: measureLog.author,
      draft: measureLog.draft,
      modifiedOn: measureLog.modifiedOn,
      modifiedBy: measureLog.modifiedBy,
      deleted: measureLog.deleted,
      deletedBy: measureLog.deletedBy,
      deletedOn: measureLog.deletedOn,
      archived: measureLog.archived,
      archivedBy: measureLog.archivedBy,
      archivedOn: measureLog.archivedOn
    };
  }

  private afterSubmissionSucceeded(): void {
    closeOffCanvas(this.offCanvasFormService, this.offcanvasService, OffCanvasPlacement.End, true);
    
    const action = this.isEditing ? 'updated' : 'created';
    const message = `${this.bizEntity.name} log ${action} successfully.`;
    
    this.toastService.toast(message);
  }

}
